import { z } from 'zod';
import { AxiosError } from 'axios';
import { APIRequestGet, APIRequestPost } from '../utils/APIRequests';

// Политика пароля realm'а Keycloak в разобранном сервером виде.
// maxLength = null — верхней границы нет.
export const PasswordPolicySchema = z.object({
    minLength: z.number(),
    maxLength: z.number().nullable(),
    minUpperCase: z.number(),
    minLowerCase: z.number(),
    minDigits: z.number(),
    minSpecialChars: z.number(),
    notUsername: z.boolean()
});

export type PasswordPolicy = z.infer<typeof PasswordPolicySchema>;

// Ответ 400 на операции с паролем: готовые сообщения для пользователя.
const ValidationErrorsSchema = z.object({
    errors: z.array(z.string())
});

export interface ChangePasswordRequestData {
    currentPassword: string;
    newPassword: string;
}

export interface ForgotPasswordRequestData {
    email: string;
}

export interface ResetPasswordRequestData {
    token: string;
    newPassword: string;
}

export function getPasswordPolicy(): Promise<PasswordPolicy> {
    return APIRequestGet('/auth/password-policy', { schema: PasswordPolicySchema })
        .then(({ data }) => data);
}

// 204 без тела — схему не передаём, иначе пустой ответ считается ошибкой.
export function changePassword(data: ChangePasswordRequestData): Promise<void> {
    return APIRequestPost('/auth/change-password', { data }).then(() => undefined);
}

export function forgotPassword(data: ForgotPasswordRequestData): Promise<void> {
    return APIRequestPost('/auth/forgot-password', { data }).then(() => undefined);
}

export function resetPassword(data: ResetPasswordRequestData): Promise<void> {
    return APIRequestPost('/auth/reset-password', { data }).then(() => undefined);
}

/** Статус ответа сервера, если запрос до него дошёл. */
export function getErrorStatus(error: unknown): number | undefined {
    return (error as AxiosError)?.response?.status;
}

/**
 * Сообщения из ответа 400. Сервер отдаёт либо `{ errors: string[] }`
 * (нарушения политики, отказ Keycloak), либо одну строку (прочие отказы).
 * Пустой массив — ответ не 400 или тело не распознано.
 */
export function getValidationErrors(error: unknown): string[] {
    if (getErrorStatus(error) !== 400) {
        return [];
    }

    const body = (error as AxiosError).response?.data;

    if (typeof body === 'string') {
        return body ? [body] : [];
    }

    const result = ValidationErrorsSchema.safeParse(body);

    return result.success ? result.data.errors : [];
}
